/**
 * hotkeyStore — the ONE module-level registry + a single global keydown
 * dispatcher for the shared hotkey layer (CLAUDE.md standing rule 14, §18.6,
 * ADR-0013).  Mirrors the loadingStore pattern: a module-level registry drives
 * behaviour, a tiny zustand store (`useHotkeyList`) publishes a display snapshot
 * that the cheat sheet subscribes to.
 *
 * Guarantees the store enforces so forms don't have to:
 *  • Editable-target guard — a plain key never fires while typing; modifier
 *    combos / Esc / F-keys still fire (so Ctrl+S works mid-typing).
 *  • Exact-modifier match — Ctrl+S never fires on Ctrl+Shift+S.
 *  • Last-registered-wins for a combo — a mounted form's binding beats an
 *    ambient one; the enabled() gate keeps a disabled def listed-but-inert.
 *  • Modal yield — while any `[aria-modal="true"]` dialog is open the store
 *    yields the keyboard to it (so a form's Esc never fires under a confirm).
 *  • Chords — `g h` = press g then h within the window; never fire in inputs.
 *
 * There is exactly ONE `keydown` listener for the whole app, added at import.
 */
import { create } from 'zustand';
import {
  parseSequence, comboMatchesEvent, comboAllowsInput, isEditableTarget, type Combo,
} from './keyCombo';

/** A hotkey binding.  `run` is called (fresh — no stale closure via useHotkeys)
 *  when the combo fires and the binding is enabled. */
export interface HotkeyDef {
  /** e.g. `"mod+s"`, `"alt+n"`, `"f2"`, `"esc"`, `"alt+left"`, or a chord `"g h"`. */
  combo: string;
  /** Imperative phrase for the cheat sheet + tooltip ("Validate"). */
  label: string;
  /** Cheat-sheet heading — the scope ("Toolbar", "Detail", "Global") or a form title. */
  group: string;
  run: (e: KeyboardEvent) => void;
  /** Inert + greyed while true (busy, no selection, view mode). */
  disabled?: boolean;
  /** Sort within the group (lower first). */
  order?: number;
  /** Override the editable-input default (see comboAllowsInput). */
  allowInInput?: boolean;
  /** Default true; set false to let the browser also handle the key. */
  preventDefault?: boolean;
  /** Register but omit from the cheat sheet (e.g. the F2 alias for Edit). */
  hidden?: boolean;
  /** The opposite of `hidden`: LISTED in the cheat sheet but NEVER matched by
   *  the store, for a key the browser already handles natively — a form's
   *  Enter = submit (the login page's Sign In). It makes the key discoverable
   *  (rule 14) without the store double-handling it; `run` is never called. */
  displayOnly?: boolean;
}

interface Registered extends HotkeyDef { id: number; seq: Combo[]; }

/** The display-safe subset the cheat sheet renders (no functions). */
export interface HotkeyListItem {
  id: number; combo: string; label: string; group: string; order: number; disabled: boolean;
}

/** Reactive snapshot for the cheat sheet — republished on every change. */
export const useHotkeyList = create<{ items: HotkeyListItem[] }>(() => ({ items: [] }));

const registry = new Map<number, Registered>();
let seqId = 0;

// ── Per-tenant key remap (ADR-0013 Phase 3 part 2) ────────────────────────────
// A map of normalized DEFAULT combo → override combo. A binding registers with
// its default combo; the store matches (and the cheat sheet / palette display)
// the EFFECTIVE combo, so remapping a semantic action (New Alt+N, Save Ctrl+S …)
// re-keys it everywhere at once. Loaded from App Config after login.
const overrides = new Map<string, string>();
const normCombo = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

/** The combo actually in force for a default (its override if set, else itself).
 *  Shared components pass their default combo here so tooltips show the real key. */
export function effectiveCombo(defaultCombo: string): string {
  return overrides.get(normCombo(defaultCombo)) ?? defaultCombo;
}

/** Replace the whole override map (from `GET /api/v1/app-config` → `hotkeys`,
 *  applied once after login) and re-derive every binding's match sequence + the
 *  display snapshot so live forms pick up the change with no remount. */
export function setHotkeyOverrides(map: Record<string, string> | null | undefined): void {
  overrides.clear();
  if (map) {
    for (const [def, ov] of Object.entries(map)) {
      const d = normCombo(def);
      const o = (ov ?? '').trim();
      if (d && o && normCombo(o) !== d) overrides.set(d, o); // a no-op remap is dropped
    }
  }
  for (const h of registry.values()) h.seq = parseSequence(effectiveCombo(h.combo));
  publish();
}

const CHORD_TIMEOUT_MS = 1200;
let armed: Registered[] = [];
let chordTimer: ReturnType<typeof setTimeout> | null = null;

function snapshotItems(): HotkeyListItem[] {
  // One entry per ACTION. The same action can be registered twice — a form that
  // draws its <AppToolbar> at the top AND the bottom (PLM, prototype v8) gets
  // every toolbar key twice — and the cheat sheet and Ctrl+K palette then listed
  // "Preview  Alt+P" twice (owner report 2026-09-26). Identical group + label +
  // key collapse to ONE row, keeping the copy the dispatcher would run: the
  // latest ENABLED one (last-registered-wins), else the latest.
  const byKey = new Map<string, HotkeyListItem>();
  for (const h of registry.values()) {
    if (h.hidden) continue;
    const item: HotkeyListItem = {
      id: h.id, combo: effectiveCombo(h.combo), label: h.label, group: h.group,
      order: h.order ?? 100, disabled: !!h.disabled,
    };
    const key = `${item.group}\u0000${item.label}\u0000${item.combo}`;
    const prev = byKey.get(key);
    if (!prev || !item.disabled || prev.disabled) byKey.set(key, item);
  }
  return [...byKey.values()];
}

function publish(): void {
  useHotkeyList.setState({ items: snapshotItems() });
}

/** Register a binding.  Returns an unregister function (call on unmount). */
export function registerHotkey(def: HotkeyDef): () => void {
  const id = ++seqId;
  registry.set(id, { ...def, id, seq: parseSequence(effectiveCombo(def.combo)) });
  publish();
  return () => unregisterHotkey(id);
}

export function unregisterHotkey(id: number): void {
  if (registry.delete(id)) publish();
}

/**
 * Invoke a registered binding by its id — used by the Ctrl+K command palette to
 * run whatever the user picked (an on-screen action or a nav chord; both live in
 * the registry). No-op if the binding is gone or disabled. Returns whether it ran.
 */
export function runHotkeyById(id: number): boolean {
  const h = registry.get(id);
  if (!h || h.disabled || h.displayOnly) return false;   // display-only: nothing to run
  h.run(new KeyboardEvent('keydown')); // most handlers ignore the event
  return true;
}

/** Test helper — clear the registry + any pending chord (see *.test.ts). */
export function __resetHotkeysForTest(): void {
  registry.clear();
  overrides.clear();
  armed = [];
  if (chordTimer) { clearTimeout(chordTimer); chordTimer = null; }
  publish();
}

/** While any modal/dialog is open the store yields the keyboard to it. */
function aModalIsOpen(): boolean {
  return typeof document !== 'undefined' && document.querySelector('[aria-modal="true"]') != null;
}

function clearArmed(): void {
  armed = [];
  if (chordTimer) { clearTimeout(chordTimer); chordTimer = null; }
}

function bestSingleMatch(e: KeyboardEvent, editable: boolean): Registered | null {
  let best: Registered | null = null;
  for (const h of registry.values()) {
    if (h.disabled || h.displayOnly || h.seq.length !== 1) continue;
    const c = h.seq[0];
    if (!c || !comboMatchesEvent(c, e)) continue;
    if (editable && !(h.allowInInput ?? comboAllowsInput(c))) continue;
    if (!best || h.id > best.id) best = h; // last-registered wins
  }
  return best;
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.defaultPrevented) return;
  const k = e.key;
  if (k === 'Shift' || k === 'Control' || k === 'Alt' || k === 'Meta') return; // lone modifier
  if (e.repeat || e.isComposing) return;
  if (aModalIsOpen()) { clearArmed(); return; } // a dialog owns the keyboard

  const editable = isEditableTarget(e.target);

  // 1) Complete a pending chord (chords never fire in inputs).
  if (armed.length) {
    let best: Registered | null = null;
    for (const h of armed) {
      if (h.disabled) continue;
      const second = h.seq[1];
      if (second && comboMatchesEvent(second, e) && (!best || h.id > best.id)) best = h;
    }
    clearArmed();
    if (best) { e.preventDefault(); best.run(e); return; }
    // no chord completion — fall through to normal handling for this key
  }

  // 2) Single-combo match.
  const m = bestSingleMatch(e, editable);
  if (m) {
    if (m.preventDefault !== false) e.preventDefault();
    m.run(e);
    return;
  }

  // 3) Start a chord (never inside an input).
  if (!editable) {
    const starters: Registered[] = [];
    for (const h of registry.values()) {
      if (h.disabled || h.displayOnly || h.seq.length !== 2) continue;
      const first = h.seq[0];
      if (first && comboMatchesEvent(first, e)) starters.push(h);
    }
    if (starters.length) {
      armed = starters;
      e.preventDefault();
      chordTimer = setTimeout(() => { armed = []; chordTimer = null; }, CHORD_TIMEOUT_MS);
    }
  }
}

// ONE global listener for the whole app (added once at import — not in an effect,
// so React StrictMode never double-binds it).
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', handleKeydown);
}
